import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

let counter = 0;

type UseDialogParams = Readonly<{
  buttonTextCancel?: string;
  buttonTextSuccess?: string;
  id?: string;
  isOpen?: boolean;
}>;

type UseDialogResult = Readonly<{
  closeLabel: string;
  descriptionId: string;
  initialFocusRef: React.RefObject<HTMLDivElement>;
  resolvedButtonTextCancel: string;
  resolvedButtonTextSuccess: string;
  titleId: string;
}>;

function useDialog({ buttonTextCancel, buttonTextSuccess, id, isOpen }: UseDialogParams): UseDialogResult {
  const { t } = useTranslation(['actions']);
  const idRef = useRef<string | null>(null);
  const initialFocusRef = useRef<HTMLDivElement>(null);

  if (idRef.current === null) {
    counter += 1;
    idRef.current = id ?? `dialog-${counter}`;
  }

  // After MUI's TrapFocus settles on Paper, hand focus to a neutral anchor so
  // :focus-visible never highlights an action button on open. Safe for
  // alertdialogs too: Enter on the anchor does not activate Confirm.
  useEffect(() => {
    if (!isOpen) return undefined;
    const rafId = globalThis.requestAnimationFrame(() => {
      initialFocusRef.current?.focus();
    });
    return () => globalThis.cancelAnimationFrame(rafId);
  }, [isOpen]);

  return {
    closeLabel: t('actions:Close'),
    descriptionId: `${idRef.current}-description`,
    initialFocusRef,
    resolvedButtonTextCancel: buttonTextCancel ?? t('actions:Cancel'),
    resolvedButtonTextSuccess: buttonTextSuccess ?? t('actions:Confirm'),
    titleId: `${idRef.current}-title`,
  };
}

export default useDialog;
